<template>
  <div class="px-6 lg:px-10 py-6">
    <h1 class="text-[26px] font-bold text-gray-900 mb-12">Payments Report</h1>

    <div v-if="!canView" class="mt-10"></div>

    <DynamicReportForm
      v-else
      v-model="model"
      :fields="fields"
      :columns="2"
      stackAt="md"
      contentWidth="2xl"
      actionLabel="View Report"
      :showTitle="false"
      :loading="loading"
      :errors="errors"
      @submit="onSubmit"
    />
  </div>
</template>

<script setup>
import { ref, watch, onMounted } from "vue"
import DynamicReportForm from "@/components/DynamicReportForm.vue"
import api from "@/api"
import { loadMyPermissions, hasAny } from "@/utils/permissions"

const canView = ref(false)
const loading = ref(false)
const errors  = ref({})

onMounted(async () => {
  await loadMyPermissions(false)
  canView.value = hasAny(["payments_report.view"])
})

const model = ref({
  from_date:      "",
  to_date:        "",
  payment_method: "",
  company_id:     "",
})

watch(
  model,
  (v) => {
    if (v.from_date)      delete errors.value.from_date
    if (v.to_date)        delete errors.value.to_date
    if (v.payment_method) delete errors.value.payment_method
    if (v.company_id !== "" && v.company_id !== null && v.company_id !== undefined)
      delete errors.value.company_id
  },
  { deep: true }
)

const fields = [
  { name: "from_date", label: "From", type: "date", span: 1, placeholder: "yyyy/mm/dd" },
  { name: "to_date",   label: "To",   type: "date", span: 1, placeholder: "yyyy/mm/dd" },

  {
    name:        "payment_method",
    label:       "Payment Method",
    type:        "select",
    span:        1,
    placeholder: "Select payment Method",
    loader: async () => [
      { label: "Cash",             value: "cash"             },
      { label: "Credit Card",      value: "credit_card"      },
      { label: "Online",           value: "online"           },
      { label: "Check",            value: "check"            },
      { label: "Smart Link",       value: "smart_link"       },
      { label: "Bank Transfer",    value: "bank_transfer"    },
      { label: "No Bank Transfer", value: "no_bank_transfer" },
    ],
  },

  {
    name:        "company_id",
    label:       "Company",
    type:        "select",
    span:        1,
    placeholder: "Select company",
    loader: async () => {
      const res = await api.get("/accounting/companies")
      return [
        { label: "All", value: "" },
        ...res.data.map(c => ({ label: c.name, value: c.id })),
      ]
    },
  },
]

function validate() {
  const e = {}
  if (!model.value.from_date)      e.from_date      = "From date is required"
  if (!model.value.to_date)        e.to_date        = "To date is required"
  if (!model.value.payment_method) e.payment_method = "Payment method is required"
  if (model.value.company_id === undefined || model.value.company_id === null)
    e.company_id = "Company is required"
  errors.value = e
  return Object.keys(e).length === 0
}

async function onSubmit(_) {
  if (!canView.value || !validate()) return

  const params = new URLSearchParams()
  if (model.value.from_date)      params.append("from",           model.value.from_date)
  if (model.value.to_date)        params.append("to",             model.value.to_date)
  if (model.value.payment_method) params.append("payment_method", model.value.payment_method)
  if (model.value.company_id)     params.append("company_id",     model.value.company_id)

  loading.value = true
  try {
    const res = await api.get(`/accounting/payments/report/pdf?${params.toString()}`, {
      responseType: "blob",
    })
    const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }))
    window.open(url, "_blank")
  } catch (err) {
    console.error("Failed to generate report:", err)
  } finally {
    loading.value = false
  }
}
</script>
